import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import paymentsService from '../services/paymentsService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import '../assets/guardianPayments.css';

const GuardianPayments = () => {
  const { guardianId: guardianIdParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if current user is a guardian (Laravel: $currentUser->hasRole('GUARDIAN'))
  const isGuardian = () => {
    if (!user) return false;
    // Check role property (string)
    if (user.role === 'GUARDIAN') return true;
    // Check roles array (array of strings or objects)
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles.some(role => {
        if (typeof role === 'string') return role === 'GUARDIAN';
        if (role && typeof role === 'object' && role.name) return role.name === 'GUARDIAN';
        return false;
      });
    }
    // Check userable_type (if user is a guardian, userable_type will be 'Guardian' or 'guardians')
    if (user.userable_type && ['Guardian', 'guardians', 'App\\Guardians\\Guardian'].includes(user.userable_type)) {
      return true;
    }
    return false;
  };

  const userIsGuardian = isGuardian();

  // Check if current user is SALES role
  const isSales = () => {
    if (!user) return false;
    if (user.role === 'SALES') return true;
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles.some(role => {
        if (typeof role === 'string') return role === 'SALES';
        if (role && typeof role === 'object' && role.name) return role.name === 'SALES';
        return false;
      });
    }
    return false;
  };

  const userIsSales = isSales();

  // Use guardianId from URL params if available, otherwise use from JWT token
  const guardianId = guardianIdParam || user?.userable_id;

  const [loading, setLoading] = useState(true);
  const [guardian, setGuardian] = useState(null);
  const [payments, setPayments] = useState([]);
  const [initialPayments, setInitialPayments] = useState([]);
  const [keyFeePayments, setKeyFeePayments] = useState([]);
  const [paymentsNotes, setPaymentsNotes] = useState('');
  // Modal removed: Now using dedicated page like Laravel

  // Pagination state for each table
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsEntriesPerPage, setPaymentsEntriesPerPage] = useState(30);
  const [paymentsSearch, setPaymentsSearch] = useState('');
  const [initialPaymentsPage, setInitialPaymentsPage] = useState(1);
  const [initialPaymentsEntriesPerPage, setInitialPaymentsEntriesPerPage] = useState(30);
  const [initialPaymentsSearch, setInitialPaymentsSearch] = useState('');
  const [keyFeePaymentsPage, setKeyFeePaymentsPage] = useState(1);
  const [keyFeePaymentsEntriesPerPage, setKeyFeePaymentsEntriesPerPage] = useState(30);
  const [keyFeePaymentsSearch, setKeyFeePaymentsSearch] = useState('');

  useEffect(() => {
    loadGuardianPayments();
  }, [guardianId]);

  const loadGuardianPayments = async () => {
    try {
      setLoading(true);
      console.log('Loading guardian payments for ID:', guardianId);
      
      const response = await paymentsService.getGuardianPayments(guardianId);
      console.log('Guardian payments response:', response);
      
      if (response.success && response.data) {
        const { guardian, payments, initialPayments, keyFeePayments } = response.data;
        console.log('Setting guardian data:', guardian);
        console.log('Setting payments:', payments);
        console.log('Setting initial payments:', initialPayments);
        console.log('Setting key fee payments:', keyFeePayments);
        
        setGuardian(guardian);
        setPayments(payments || []);
        setInitialPayments(initialPayments || []);
        setKeyFeePayments(keyFeePayments || []);
        setPaymentsNotes(guardian?.payments_notes || '');
      } else {
        console.error('Invalid response format:', response);
        toast.error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error loading guardian payments:', error);
      toast.error('Failed to load guardian payments - check console for details');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const blob = await paymentsService.downloadGuardianPayments(guardianId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `guardian_${guardianId}_payments.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export CSV');
    }
  };

  // Handle notes change - update local state only (for immediate UI feedback)
  const handleNotesChange = (e) => {
    const notes = e.target.value;
    setPaymentsNotes(notes);
  };

  // Handle notes blur - save to backend when user leaves the field
  const handleNotesBlur = async (e) => {
    const notes = e.target.value;
    
    if (!guardianId) {
      console.error('Cannot update notes: guardianId not found');
      toast.error('Guardian ID not found');
      return;
    }

    try {
      console.log('💾 [GUARDIAN PAYMENTS] Saving notes for guardian:', guardianId);
      const response = await paymentsService.updateGuardianPaymentsNotes(guardianId, notes);
      
      if (response && response.success) {
        console.log('✅ [GUARDIAN PAYMENTS] Notes saved successfully');
        toast.success('Payments notes updated successfully');
        // Reload to get updated editor info
        await loadGuardianPayments();
      } else {
        throw new Error(response?.message || 'Failed to update notes');
      }
    } catch (error) {
      console.error('❌ [GUARDIAN PAYMENTS] Error updating notes:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to update notes');
      // Revert to original value on error
      setPaymentsNotes(guardian?.payments_notes || '');
    }
  };

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Helper function to filter payments based on search term
  const filterPayments = (payments, searchTerm) => {
    if (!searchTerm) return payments;
    const search = searchTerm.toLowerCase();
    return payments.filter(payment => {
      return (
        formatDate(payment.due_date).toLowerCase().includes(search) ||
        formatCurrency(payment.fee).toLowerCase().includes(search) ||
        (payment.payment_status || '').toLowerCase().includes(search) ||
        formatCurrency(payment.amount_paid).toLowerCase().includes(search) ||
        formatCurrency(payment.to_pay).toLowerCase().includes(search) ||
        formatDate(payment.payment_date).toLowerCase().includes(search) ||
        (payment.property || '').toLowerCase().includes(search) ||
        (payment.rent_status || '').toLowerCase().includes(search) ||
        (payment.type || '').toLowerCase().includes(search) ||
        (payment.key_fee || '').toLowerCase().includes(search) ||
        formatCurrency(payment.paid_from_previous_rent).toLowerCase().includes(search)
      );
    });
  };

  // Helper function to render pagination controls
  const renderPagination = (currentPage, setCurrentPage, entriesPerPage, totalRecords, tableName) => {
    if (totalRecords <= entriesPerPage) return null;
    
    const totalPages = Math.ceil(totalRecords / entriesPerPage);
    const pages = [];
    const maxPagesToShow = 5;
    const halfRange = Math.floor(maxPagesToShow / 2);
    
    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);
    
    if (currentPage <= halfRange) {
      endPage = Math.min(maxPagesToShow, totalPages);
    }
    if (currentPage + halfRange >= totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }
    
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => setCurrentPage(1)}
          style={{
            padding: '6px 12px',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            backgroundColor: 'white',
            color: '#495057',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" style={{ padding: '0 4px' }}>...</span>);
      }
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          style={{
            padding: '6px 12px',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            backgroundColor: i === currentPage ? '#007bff' : 'white',
            color: i === currentPage ? 'white' : '#495057',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: i === currentPage ? '600' : 'normal'
          }}
        >
          {i}
        </button>
      );
    }
    
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" style={{ padding: '0 4px' }}>...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
          style={{
            padding: '6px 12px',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            backgroundColor: 'white',
            color: '#495057',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          {totalPages}
        </button>
      );
    }
    
    return (
      <div style={{
        padding: '15px 20px',
        backgroundColor: '#f8f9fa',
        borderTop: '1px solid #dee2e6',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ fontSize: '14px', color: '#666' }}>
          Showing {((currentPage - 1) * entriesPerPage) + 1} to {Math.min(currentPage * entriesPerPage, totalRecords)} of {totalRecords} entries
        </div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '6px 12px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
              color: currentPage === 1 ? '#6c757d' : '#495057',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Previous
          </button>
          {pages}
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            style={{
              padding: '6px 12px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: currentPage >= totalPages ? '#f8f9fa' : 'white',
              color: currentPage >= totalPages ? '#6c757d' : '#495057',
              cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const getPaymentStatusBadge = (status) => {
    // Laravel config/rents.php payment_status_label mapping:
    // 0 => 'danger' (Not paid), 1 => 'success' (Paid), 2 => 'warning' (To pay),
    // 3 => 'default' (Ignore), 4 => 'info' (Payment Plan), 5 => 'primary' (Legal), 6 => 'notice-to-quit' (Notice to Quit)
    const statusMap = {
      'Not paid': 'badge-danger',      // Laravel: 0 => 'danger'
      'Paid': 'badge-success',         // Laravel: 1 => 'success'
      'To pay': 'badge-warning',       // Laravel: 2 => 'warning'
      'Ignore': 'badge-default',       // Laravel: 3 => 'default'
      'Payment Plan': 'badge-info',    // Laravel: 4 => 'info'
      'Legal': 'badge-primary',        // Laravel: 5 => 'primary'
      'Notice to Quit': 'badge-notice-to-quit', // Laravel: 6 => 'notice-to-quit'
      'Overdue': 'badge-danger',       // Additional status
      'Pending': 'badge-info'          // Additional status
    };
    
    return (
      <span className={`badge ${statusMap[status] || 'badge-secondary'}`}>
        {status}
      </span>
    );
  };

  const getRentStatusBadge = (status) => {
    // Laravel rent status mapping (from config/rents.php):
    // 0 => 'in_progress' (In Progress), 1 => 'active' (Active),
    // 2 => 'failed' (Failed), 3 => 'ended' (Ended)
    const statusMap = {
      'Active': 'badge-success',      // Laravel: 1 => 'active'
      'In Progress': 'badge-info',     // Laravel: 0 => 'in_progress'
      'Failed': 'badge-danger',       // Laravel: 2 => 'failed'
      'Ended': 'badge-secondary',     // Laravel: 3 => 'ended'
      'Inactive': 'badge-danger',     // Additional status
      'Pending': 'badge-warning'       // Additional status
    };
    
    return (
      <span className={`badge ${statusMap[status] || 'badge-secondary'}`}>
        {status}
      </span>
    );
  };

  const handleAddPayment = () => {
    if (guardian?.latest_not_ended_rent?.id) {
      navigate(`/rents/${guardian.latest_not_ended_rent.id}/payments/create`);
    } else {
      toast.error('No active rent found for this guardian');
    }
  };

  const handleEditPayment = (payment) => {
    navigate(`/rents/${payment.rent_id}/payments/${payment.id}/edit`);
  };

  const handleMarkAsPaid = (payment, isInitialPayment = false) => {
    // Laravel behavior: Navigate to dedicated mark-as-paid page instead of modal
    // Laravel route format: /rents/{rentId}/payments/{paymentId}/mark-as-paid
    const rentId = payment.rent_id || payment.rentId || payment.rent?.id;
    
    if (!rentId) {
      toast.error("Cannot mark as paid: Rent ID not found for this payment");
      console.error("Payment object missing rent_id:", payment);
      return;
    }
    
    if (!payment.id) {
      toast.error("Cannot mark as paid: Payment ID not found");
      console.error("Payment object missing id:", payment);
      return;
    }
    
    if (isInitialPayment) {
      // Laravel route: /rents/{rentId}/payments/mark-as-paid-initial/{paymentId}
      navigate(`/rents/${rentId}/payments/mark-as-paid-initial/${payment.id}`);
    } else {
      // Laravel route: /rents/{rentId}/payments/{paymentId}/mark-as-paid
      navigate(`/rents/${rentId}/payments/${payment.id}/mark-as-paid`);
    }
  };

  const handleMarkAsPaidSuccess = async () => {
    await loadGuardianPayments(); // Refresh the data
  };

  const handleDeletePayment = async (payment, isInitialPayment = false) => {
    const amount = payment.amount || payment.fee || 0;
    if (window.confirm(`Are you sure you want to delete this payment of £${amount}? This action cannot be undone.`)) {
      try {
        console.log("GuardianPayments: Deleting payment", {
          rentId: payment.rent_id,
          paymentId: payment.id,
          isInitialPayment
        });
        
        // Use appropriate delete method based on payment type
        const response = isInitialPayment
          ? await paymentsService.deleteInitialPayment(payment.rent_id, payment.id)
          : await paymentsService.deletePaymentWithRent(payment.rent_id, payment.id);

        if (response && response.success) {
          toast.success("Payment deleted successfully!");
          await loadGuardianPayments(); // Refresh the data
        } else {
          toast.error(response?.message || "Failed to delete payment");
        }
      } catch (error) {
        console.error("Error deleting payment:", error);
        toast.error("Failed to delete payment");
      }
    }
  };

  const handleSendReminder = async (payment) => {
    if (window.confirm(`Are you sure you want to send a payment reminder for £${payment.fee}?`)) {
      try {
        console.log("GuardianPayments: Sending payment reminder", {
          paymentId: payment.id
        });
        const response = await paymentsService.sendPaymentReminder(payment.id);

        if (response && response.success) {
          toast.success("Payment reminder sent successfully!");
        } else {
          toast.error(response?.message || "Failed to send payment reminder");
        }
      } catch (error) {
        console.error("Error sending payment reminder:", error);
        toast.error("Failed to send payment reminder");
      }
    }
  };

  const handleEditInitialPayment = (payment) => {
    navigate(`/rents/${payment.rent_id}/payments/${payment.id}/edit-initial`);
  };

  if (loading) {
    return (
      <div className="guardian-payments-container">
        <div className="loading">Loading guardian payments...</div>
      </div>
    );
  }

  // Don't return early if guardian is null - let the component render with empty data

  return (
    <div className="guardian-payments-container">
      {/* Breadcrumb - Laravel: guardian.payments-for-guardian (Dashboard / Payments) for guardians, guardian.payments (Dashboard / Guardians / {guardian} / Payments) for others */}
      <div className="breadcrumb">
        {userIsGuardian ? (
          // Laravel: guardian.payments-for-guardian - Dashboard / Payments
          <>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span>
            <span> / </span>
            <span>Payments</span>
          </>
        ) : (
          // Laravel: guardian.payments - Dashboard / Guardians / {guardian} / Payments
          <>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span>
            <span> / </span>
            <span onClick={() => navigate('/guardians')}>Guardians</span>
            <span> / </span>
            <span onClick={() => navigate(`/guardians/${guardianId}`)}>
              {guardian ? `${guardian.first_name} ${guardian.last_name}` : 'Guardian'}
            </span>
            <span> / </span>
            <span>Payments</span>
          </>
        )}
      </div>

      {/* Export Button - Laravel: Only shown for non-guardian users */}
      {!userIsGuardian && (
        <div className="export-section">
          <button 
            className="btn btn-head btn-standard" 
            onClick={handleExportCSV}
          >
            Export to CSV <i className="glyphicon glyphicon-triangle-right"></i>
          </button>
        </div>
      )}

      {/* Regular Payments Table */}
      <div className="payments-section">
        <div className="panel-title title-standard mt-30">
          <h3 className="mr-auto">Payments</h3>
          {guardian?.latest_not_ended_rent && !userIsSales && !userIsGuardian && (
            <button 
              className="btn btn-secondary ml-2 d-print-none"
              onClick={handleAddPayment}
            >
              <i className="glyphicon glyphicon-plus"></i> Add payment
            </button>
          )}
        </div>
        
        <div className="panel-group">
          {payments.length > 0 ? (
            <>
              {/* DataTables Control Bar - Show entries and Search */}
              <div style={{ 
                padding: '10px 20px', 
                backgroundColor: '#f8f9fa', 
                borderBottom: '1px solid #dee2e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ margin: 0, fontSize: '14px', color: '#495057' }}>Show</label>
                  <select
                    value={paymentsEntriesPerPage}
                    onChange={(e) => {
                      setPaymentsEntriesPerPage(Number(e.target.value));
                      setPaymentsPage(1);
                    }}
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                    <option value={75}>75</option>
                    <option value={100}>100</option>
                  </select>
                  <label style={{ margin: 0, fontSize: '14px', color: '#495057' }}>entries</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ margin: 0, fontSize: '14px', color: '#495057' }}>Search:</label>
                  <input
                    type="text"
                    placeholder="Search payments..."
                    value={paymentsSearch}
                    onChange={(e) => {
                      setPaymentsSearch(e.target.value);
                      setPaymentsPage(1);
                    }}
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px',
                      width: '200px'
                    }}
                  />
                </div>
              </div>

              <div className="dataTable_wrapper">
                <table className="table table-hover dt-responsive payments-table">
                <thead>
                  <tr>
                    <th>Due Date</th>
                    <th>Fee</th>
                    <th title="Payment Status">Status</th>
                    <th>Paid</th>
                    <th>To Pay</th>
                    <th>Payment Date</th>
                    <th>Property</th>
                    <th title="Rent Status">R. Status</th>
                    {!userIsSales && !userIsGuardian && <th className="action-head">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Filter payments based on search term
                    const filteredPayments = payments.filter(payment => {
                      if (!paymentsSearch) return true;
                      const searchLower = paymentsSearch.toLowerCase();
                      return (
                        formatDate(payment.due_date).toLowerCase().includes(searchLower) ||
                        formatCurrency(payment.fee).toLowerCase().includes(searchLower) ||
                        payment.payment_status?.toLowerCase().includes(searchLower) ||
                        formatCurrency(payment.amount_paid).toLowerCase().includes(searchLower) ||
                        formatCurrency(payment.to_pay).toLowerCase().includes(searchLower) ||
                        formatDate(payment.payment_date).toLowerCase().includes(searchLower) ||
                        payment.property?.toLowerCase().includes(searchLower) ||
                        payment.rent_status?.toLowerCase().includes(searchLower)
                      );
                    });
                    
                    const startIndex = (paymentsPage - 1) * paymentsEntriesPerPage;
                    const endIndex = startIndex + paymentsEntriesPerPage;
                    const paginatedPayments = filteredPayments.slice(startIndex, endIndex);
                    return paginatedPayments.map((payment, index) => (
                      <tr key={payment.id} className={(startIndex + index) % 2 === 0 ? 'even' : 'odd'}>
                      <td>{formatDate(payment.due_date)}</td>
                      <td>{formatCurrency(payment.fee)}</td>
                      <td>{getPaymentStatusBadge(payment.payment_status)}</td>
                      <td>{formatCurrency(payment.amount_paid)}</td>
                      <td>{formatCurrency(payment.to_pay)}</td>
                      <td>{formatDate(payment.payment_date)}</td>
                      <td>{payment.property}</td>
                      <td>{getRentStatusBadge(payment.rent_status)}</td>
                      <td>
                        {!userIsSales && !userIsGuardian && (
                          <div className="action-buttons">
                            {payment.payment_status !== 'Paid' && payment.status !== 1 && (
                              <button
                                className="btn btn-sm btn-success btn-action"
                                onClick={() => handleMarkAsPaid(payment)}
                                title="Mark as Paid"
                              >
                                Mark Paid
                              </button>
                            )}
                            {payment.payment_status !== 'Paid' && payment.status !== 1 && payment.to_pay > 0 && (
                              <button
                                className="btn btn-sm btn-info btn-action"
                                onClick={() => handleSendReminder(payment)}
                                title="Send Reminder"
                              >
                                Send Reminder
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-primary btn-action"
                              onClick={() => handleEditPayment(payment)}
                              title="Edit Payment"
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger btn-action"
                              onClick={() => handleDeletePayment(payment, false)}
                              title="Delete Payment"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {renderPagination(
              paymentsPage, 
              setPaymentsPage, 
              paymentsEntriesPerPage, 
              payments.filter(payment => {
                if (!paymentsSearch) return true;
                const searchLower = paymentsSearch.toLowerCase();
                return (
                  formatDate(payment.due_date).toLowerCase().includes(searchLower) ||
                  formatCurrency(payment.fee).toLowerCase().includes(searchLower) ||
                  payment.payment_status?.toLowerCase().includes(searchLower) ||
                  formatCurrency(payment.amount_paid).toLowerCase().includes(searchLower) ||
                  formatCurrency(payment.to_pay).toLowerCase().includes(searchLower) ||
                  formatDate(payment.payment_date).toLowerCase().includes(searchLower) ||
                  payment.property?.toLowerCase().includes(searchLower) ||
                  payment.rent_status?.toLowerCase().includes(searchLower)
                );
              }).length, 
              'payments'
            )}
            </>
          ) : (
            <div className="no-data">No payments!</div>
          )}
        </div>
      </div>

      {/* Initial Payments Table */}
      <div className="payments-section">
        <div className="panel-title title-standard mt-30">
          <h3>Initial Payment</h3>
        </div>
        
        <div className="panel-group">
          {/* DataTables Control Bar - Show entries and Search - Always visible (Laravel behavior) */}
          <div style={{ 
            padding: '10px 20px', 
            backgroundColor: '#f8f9fa', 
            borderBottom: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ margin: 0, fontSize: '14px', color: '#495057' }}>Show</label>
              <select
                value={initialPaymentsEntriesPerPage}
                onChange={(e) => {
                  setInitialPaymentsEntriesPerPage(Number(e.target.value));
                  setInitialPaymentsPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={75}>75</option>
                <option value={100}>100</option>
              </select>
              <label style={{ margin: 0, fontSize: '14px', color: '#495057' }}>entries</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ margin: 0, fontSize: '14px', color: '#495057' }}>Search:</label>
              <input
                type="text"
                placeholder="Search initial payments..."
                value={initialPaymentsSearch}
                onChange={(e) => {
                  setInitialPaymentsSearch(e.target.value);
                  setInitialPaymentsPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  width: '200px'
                }}
              />
            </div>
          </div>

          {initialPayments.length > 0 ? (
            <>

              <div className="dataTable_wrapper">
                <table className="table table-hover dt-responsive payments-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Due Date</th>
                    <th>Fee</th>
                    <th title="Payment Status">Status</th>
                    <th>Paid</th>
                    <th>To Pay</th>
                    <th>Payment Date</th>
                    <th>Property</th>
                    <th title="Rent Status">R. Status</th>
                    {!userIsSales && !userIsGuardian && <th className="action-head">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Filter initial payments based on search term
                    const filteredInitialPayments = initialPayments.filter(payment => {
                      if (!initialPaymentsSearch) return true;
                      const searchLower = initialPaymentsSearch.toLowerCase();
                      return (
                        payment.type?.toLowerCase().includes(searchLower) ||
                        formatDate(payment.due_date).toLowerCase().includes(searchLower) ||
                        formatCurrency(payment.fee).toLowerCase().includes(searchLower) ||
                        payment.payment_status?.toLowerCase().includes(searchLower) ||
                        formatCurrency(payment.amount_paid).toLowerCase().includes(searchLower) ||
                        formatCurrency(payment.to_pay).toLowerCase().includes(searchLower) ||
                        formatDate(payment.payment_date).toLowerCase().includes(searchLower) ||
                        payment.property?.toLowerCase().includes(searchLower) ||
                        payment.rent_status?.toLowerCase().includes(searchLower)
                      );
                    });
                    
                    const startIndex = (initialPaymentsPage - 1) * initialPaymentsEntriesPerPage;
                    const endIndex = startIndex + initialPaymentsEntriesPerPage;
                    const paginatedInitialPayments = filteredInitialPayments.slice(startIndex, endIndex);
                    return paginatedInitialPayments.map((payment, index) => (
                      <tr key={payment.id} className={(startIndex + index) % 2 === 0 ? 'even' : 'odd'}>
                      <td>{payment.type}</td>
                      <td>{formatDate(payment.due_date)}</td>
                      <td>{formatCurrency(payment.fee)}</td>
                      <td>{getPaymentStatusBadge(payment.payment_status)}</td>
                      <td>{formatCurrency(payment.amount_paid)}</td>
                      <td>{formatCurrency(payment.to_pay)}</td>
                      <td>{formatDate(payment.payment_date)}</td>
                      <td>{payment.property}</td>
                      <td>{getRentStatusBadge(payment.rent_status)}</td>
                      <td>
                        {!userIsSales && !userIsGuardian && (
                          <div className="action-buttons">
                            {payment.payment_status !== 'Paid' && payment.status !== 1 && (
                              <button
                                className="btn btn-sm btn-success btn-action"
                                onClick={() => handleMarkAsPaid(payment, true)}
                                title="Mark as Paid"
                              >
                                Mark Paid
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-primary btn-action"
                              onClick={() => handleEditInitialPayment(payment)}
                              title="Edit Initial Payment"
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger btn-action"
                              onClick={() => handleDeletePayment(payment, true)}
                              title="Delete Payment"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {renderPagination(
              initialPaymentsPage, 
              setInitialPaymentsPage, 
              initialPaymentsEntriesPerPage, 
              initialPayments.filter(payment => {
                if (!initialPaymentsSearch) return true;
                const searchLower = initialPaymentsSearch.toLowerCase();
                return (
                  payment.type?.toLowerCase().includes(searchLower) ||
                  formatDate(payment.due_date).toLowerCase().includes(searchLower) ||
                  formatCurrency(payment.fee).toLowerCase().includes(searchLower) ||
                  payment.payment_status?.toLowerCase().includes(searchLower) ||
                  formatCurrency(payment.amount_paid).toLowerCase().includes(searchLower) ||
                  formatCurrency(payment.to_pay).toLowerCase().includes(searchLower) ||
                  formatDate(payment.payment_date).toLowerCase().includes(searchLower) ||
                  payment.property?.toLowerCase().includes(searchLower) ||
                  payment.rent_status?.toLowerCase().includes(searchLower)
                );
              }).length, 
              'initialPayments'
            )}
            </>
          ) : (
            <div className="no-data">No payments!</div>
          )}
        </div>
      </div>

      {/* Key Fee Payments Table */}
      <div className="payments-section">
        <div className="panel-title title-standard mt-30">
          <h3>Key Fee</h3>
        </div>
        
        <div className="panel-group">
          {/* DataTables Control Bar - Show entries and Search - Always visible (Laravel behavior) */}
          <div style={{ 
            padding: '10px 20px', 
            backgroundColor: '#f8f9fa', 
            borderBottom: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ margin: 0, fontSize: '14px', color: '#495057' }}>Show</label>
              <select
                value={keyFeePaymentsEntriesPerPage}
                onChange={(e) => {
                  setKeyFeePaymentsEntriesPerPage(Number(e.target.value));
                  setKeyFeePaymentsPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={75}>75</option>
                <option value={100}>100</option>
              </select>
              <label style={{ margin: 0, fontSize: '14px', color: '#495057' }}>entries</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ margin: 0, fontSize: '14px', color: '#495057' }}>Search:</label>
              <input
                type="text"
                placeholder="Search key fee payments..."
                value={keyFeePaymentsSearch}
                onChange={(e) => {
                  setKeyFeePaymentsSearch(e.target.value);
                  setKeyFeePaymentsPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  width: '200px'
                }}
              />
            </div>
          </div>

          {keyFeePayments.length > 0 ? (
            <>

              <div className="dataTable_wrapper">
                <table className="table table-hover dt-responsive payments-table">
                <thead>
                  <tr>
                    <th>Due Date</th>
                    <th>Key Fee</th>
                    <th title="Payment Status">Status</th>
                    <th>Paid From Previous Rent</th>
                    <th>To Pay</th>
                    <th>Paid</th>
                    <th>Payment Date</th>
                    <th>Property</th>
                    <th title="Rent Status">R. Status</th>
                    {!userIsSales && !userIsGuardian && <th className="action-head">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Filter key fee payments based on search term
                    const filteredKeyFeePayments = keyFeePayments.filter(payment => {
                      if (!keyFeePaymentsSearch) return true;
                      const searchLower = keyFeePaymentsSearch.toLowerCase();
                      return (
                        formatDate(payment.due_date).toLowerCase().includes(searchLower) ||
                        formatCurrency(payment.key_fee).toLowerCase().includes(searchLower) ||
                        payment.payment_status?.toLowerCase().includes(searchLower) ||
                        formatCurrency(payment.paid_from_previous_rent).toLowerCase().includes(searchLower) ||
                        formatCurrency(payment.to_pay).toLowerCase().includes(searchLower) ||
                        formatCurrency(payment.amount_paid).toLowerCase().includes(searchLower) ||
                        formatDate(payment.payment_date).toLowerCase().includes(searchLower) ||
                        payment.property?.toLowerCase().includes(searchLower) ||
                        payment.rent_status?.toLowerCase().includes(searchLower)
                      );
                    });
                    
                    const startIndex = (keyFeePaymentsPage - 1) * keyFeePaymentsEntriesPerPage;
                    const endIndex = startIndex + keyFeePaymentsEntriesPerPage;
                    const paginatedKeyFeePayments = filteredKeyFeePayments.slice(startIndex, endIndex);
                    return paginatedKeyFeePayments.map((payment, index) => (
                      <tr key={payment.id} className={(startIndex + index) % 2 === 0 ? 'even' : 'odd'}>
                      <td>{formatDate(payment.due_date)}</td>
                      <td>{formatCurrency(payment.key_fee)}</td>
                      <td>{getPaymentStatusBadge(payment.payment_status)}</td>
                      <td>{formatCurrency(payment.paid_from_previous_rent)}</td>
                      <td>{formatCurrency(payment.to_pay)}</td>
                      <td>{formatCurrency(payment.amount_paid)}</td>
                      <td>{formatDate(payment.payment_date)}</td>
                      <td>{payment.property}</td>
                      <td>{getRentStatusBadge(payment.rent_status)}</td>
                      <td>
                        {!userIsSales && !userIsGuardian && (
                          <div className="action-buttons">
                            <button
                              className="btn btn-sm btn-primary btn-action"
                              onClick={() => handleEditInitialPayment(payment)}
                              title="Edit Key Fee Payment"
                            >
                              Edit
                            </button>
                            {payment.payment_status !== 'Paid' && payment.status !== 1 && (
                              <button
                                className="btn btn-sm btn-success btn-action"
                                onClick={() => handleMarkAsPaid(payment)}
                                title="Mark as Paid"
                              >
                                Mark Paid
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {renderPagination(
              keyFeePaymentsPage, 
              setKeyFeePaymentsPage, 
              keyFeePaymentsEntriesPerPage, 
              keyFeePayments.filter(payment => {
                if (!keyFeePaymentsSearch) return true;
                const searchLower = keyFeePaymentsSearch.toLowerCase();
                return (
                  formatDate(payment.due_date).toLowerCase().includes(searchLower) ||
                  formatCurrency(payment.key_fee).toLowerCase().includes(searchLower) ||
                  payment.payment_status?.toLowerCase().includes(searchLower) ||
                  formatCurrency(payment.paid_from_previous_rent).toLowerCase().includes(searchLower) ||
                  formatCurrency(payment.to_pay).toLowerCase().includes(searchLower) ||
                  formatCurrency(payment.amount_paid).toLowerCase().includes(searchLower) ||
                  formatDate(payment.payment_date).toLowerCase().includes(searchLower) ||
                  payment.property?.toLowerCase().includes(searchLower) ||
                  payment.rent_status?.toLowerCase().includes(searchLower)
                );
              }).length, 
              'keyFeePayments'
            )}
            </>
          ) : (
            <div className="no-data">No payments!</div>
          )}
        </div>
      </div>

      {/* Payments Notes Section - hidden for guardian users (Laravel: only staff can edit notes) */}
      {!userIsGuardian && (
        <div className="payments-notes-section">
          <div className="panel-title title-standard">
            <h3>Payments notes</h3>
          </div>
          <div className="panel-group border-down margin-down">
            <textarea
              className="form-control width-100"
              rows="10"
              value={paymentsNotes}
              onChange={handleNotesChange}
              onBlur={handleNotesBlur}
              placeholder="Enter payments notes..."
            />
            
            <span className="edited-info pull-left">
              <i>
                Last edited by {guardian?.paymentsNotesEditor?.full_name || 'Unknown'}
                {guardian?.payments_notes_edited_at &&
                  ` at ${new Date(guardian.payments_notes_edited_at).toLocaleString()}`
                }
              </i>
            </span>
          </div>
        </div>
      )}

      {/* Mark as Paid Modal - Removed: Now using dedicated page like Laravel */}
    </div>
  );
};

export default GuardianPayments; 